import {
  GOOGLE_AUTH_LOGIN,
  GOOGLE_AUTH_SIGNUP,
  REVOKE_USER_IDENTITY,
  USER_ACCEPT_COMPANY_INVITATION,
  USER_ACCEPT_EMAIL_INVITE_TOKEN,
  USER_EMAIL_VERIFICATION_RESEND,
  USER_FORGOT_PASSWORD,
  USER_LOG_IN,
  USER_REFRESH_CLAIMS,
  USER_REFRESH_TOKEN,
  USER_RESET_PASSWORD,
  USER_SIGN_UP,
  USER_VALIDATE_EMAIL_VERIFICATION_TOKEN,
  USER_VALIDATE_RESET_PASSWORD_TOKEN,
  USER_VERIFY_EMAIL_INVITE_TOKEN,
} from '@/constants/api';
import { User } from '@/models/User';
import {
  AcceptCompanyInvitationPayload,
  AcceptEmailInviteToken,
  LoginCredentials,
  LoginResponse,
  RefreshTokenResponse,
  ResetPasswordPayload,
  SignUpPayload,
  SignUpResponse,
  ValidateEmailVerificationToken,
  ValidateResetPasswordTokenPayload,
} from '@/types/auth';

import { save } from '@/utils/request';
import { REQUEST_METHOD } from '@treyd-io/core/constants/request';

export const googleSignIn = async ({
  token,
  sms_last_login,
}: {
  token: string;
  sms_last_login?: string;
}) =>
  save<LoginResponse>({
    url: GOOGLE_AUTH_LOGIN,
    data: {
      google_access_token: token,
      sms_last_login,
    },
    method: REQUEST_METHOD.POST,
    options: {
      noAuth: true,
    },
  });

export const googleSignUp = async ({ token }: { token: string }) =>
  save<LoginResponse>({
    url: GOOGLE_AUTH_SIGNUP,
    data: { google_access_token: token },
    method: REQUEST_METHOD.POST,
    options: {
      noAuth: true,
    },
  });

export const refreshAccessToken = async (refreshToken: string) =>
  save<RefreshTokenResponse>({
    url: USER_REFRESH_TOKEN,
    data: {
      refresh: refreshToken,
    },
    method: REQUEST_METHOD.POST,
    options: {
      noAuth: true,
    },
  });

export const credentialsSignIn = async (values: LoginCredentials) =>
  save<LoginResponse>({
    url: USER_LOG_IN,
    data: {
      email: values.email?.toLowerCase(),
      password: values.password,
      sms_last_login: values.sms_last_login,
    },
    method: REQUEST_METHOD.POST,
    options: {
      noAuth: true,
    },
  });

export const credentialsSignUp = (payload: SignUpPayload) =>
  save<SignUpResponse>({
    url: USER_SIGN_UP,
    data: payload,
    method: REQUEST_METHOD.POST,
    options: {
      noAuth: true,
    },
  });

export const refreshClaims = async (companyId?: number) =>
  save<LoginResponse>({
    url: USER_REFRESH_CLAIMS,
    data: { company_id: companyId },
    method: REQUEST_METHOD.POST,
  });

export const forgotPassword = async (email: string) =>
  save<{ email: string }>({
    url: USER_FORGOT_PASSWORD,
    data: { email },
    method: REQUEST_METHOD.POST,
    options: {
      noAuth: true,
    },
  });

export const resetPassword = async (payload: ResetPasswordPayload) =>
  save<{ email: string }>({
    url: USER_RESET_PASSWORD,
    data: payload,
    method: REQUEST_METHOD.POST,
    options: {
      noAuth: true,
    },
  });

export const revokeUserIdentity = async () =>
  save({
    url: REVOKE_USER_IDENTITY,
    method: REQUEST_METHOD.POST,
  });

export const validateResetPasswordToken = async (
  payload: ValidateResetPasswordTokenPayload
) =>
  save<{ email: string }>({
    url: USER_VALIDATE_RESET_PASSWORD_TOKEN,
    data: payload,
    method: REQUEST_METHOD.PUT,
    options: {
      noAuth: true,
    },
  });

export const validateEmailVerificationToken = async (
  payload: ValidateEmailVerificationToken
) =>
  save<{ access_token: string; refresh_token: string }>({
    url: USER_VALIDATE_EMAIL_VERIFICATION_TOKEN,
    data: payload,
    method: REQUEST_METHOD.POST,
    options: {
      noAuth: true,
    },
  });

export const acceptEmailInviteToken = async (payload: AcceptEmailInviteToken) =>
  save<
    User & {
      access_token: string;
      refresh_token: string;
    }
  >({
    url: USER_ACCEPT_EMAIL_INVITE_TOKEN,
    data: payload,
    method: REQUEST_METHOD.PUT,
    options: {
      noAuth: true,
    },
  });

export const acceptCompanyInvitation = async (
  payload: AcceptCompanyInvitationPayload
) =>
  save({
    url: USER_ACCEPT_COMPANY_INVITATION,
    data: payload,
    method: REQUEST_METHOD.POST,
  });

export const validateInviteToken = async (token: string) =>
  save<{ has_password: boolean }>({
    url: USER_VERIFY_EMAIL_INVITE_TOKEN,
    data: { token },
    method: REQUEST_METHOD.PUT,
    options: {
      noAuth: true,
    },
  });

export const resendEmailVerification = async () =>
  save({
    url: USER_EMAIL_VERIFICATION_RESEND,
    method: REQUEST_METHOD.POST,
  });
